'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { pick, bindAll } from 'underscore';
import text from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import OccurrencesTableRow from 'ContentComponents/cmv2/occurrences/OccurrencesTableRow';
import OccurrenceLabel from 'ContentComponents/cmv2/occurrences/OccurrenceLabel';
import { RESOURCES_BY_TYPE } from 'ContentComponents/cmv2/constants';
import UIToggle from 'UIComponents/input/UIToggle';
import UIList from 'UIComponents/list/UIList';
import UIFlex from 'UIComponents/layout/UIFlex';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UITable from 'UIComponents/table/UITable';
import UIButton from 'UIComponents/button/UIButton';
import UINumberInput from 'UIComponents/input/UINumberInput';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UILink from 'UIComponents/link/UILink';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import Small from 'UIComponents/elements/Small';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { OCCURRENCE } from 'DesignData/enums/CustomWidgetFieldAttributes';
import ToolExpandableSection from 'DesignComponents/tool/ToolExpandableSection';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import ErrorIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/ErrorIcon';
import WarningIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/WarningIcon';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import { MODULE_VALIDATION_OCCURRENCE_MAX_LESS_THAN_MIN, MODULE_VALIDATION_OCCURRENCE_MAX_LESS_THAN_ONE, MODULE_VALIDATION_OCCURRENCE_MIN_LESS_THAN_ZERO } from 'DesignData/enums/CustomWidgetFieldErrorTypes';

const sidebarI18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.${key}`;

const i18nKey = key => sidebarI18nKey(`occurrence.${key}`);

const NULLABLE_OCCURRENCE_PROPS = ['max'];

class RepeaterOptions extends PureComponent {
  constructor(props) {
    super(props);
    bindAll(this, ['getState', 'onToggleClick', 'onNumberValueChange', 'onSortingLabelFieldChange', 'onChange']);
    this.state = this.getState(props.field);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.field.id !== this.props.field.id) {
      const newState = this.getState(this.props.field);
      this.setState(newState);
    }
  }

  getState(field) {
    const occurrence = field[OCCURRENCE];
    return {
      enabled: !!occurrence,
      occurrence: occurrence || {}
    };
  }

  onToggleClick(e) {
    const checked = e.target.checked;
    this.setState({
      enabled: checked
    });
    this.props.editField({
      [OCCURRENCE]: checked ? this.state.occurrence : null
    });
  }

  onNumberValueChange(occurrenceProperty, e) {
    const value = e.target.value;
    const valueIsNullable = NULLABLE_OCCURRENCE_PROPS.indexOf(occurrenceProperty) !== -1;

    if (valueIsNullable && !value || value >= 0) {
      this.onChange(occurrenceProperty, value);
    }
  }

  onSortingLabelFieldChange(e) {
    this.onChange('sorting_label_field', e.target.value);
  }

  onChange(occurrenceProperty, value) {
    const occurrence = this.props.field[OCCURRENCE] || {};
    this.setState({
      enabled: true,
      [OCCURRENCE]: Object.assign({}, this.state.occurrence, {
        [occurrenceProperty]: value
      })
    });
    this.props.editField({
      [OCCURRENCE]: Object.assign({}, occurrence, {
        [occurrenceProperty]: value
      })
    });
  }

  renderOccurrenceHelpTooltip() {
    const title = /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('helpTooltip.explanation')
          })
        }), /*#__PURE__*/_jsxs(UIList, {
          styled: true,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('helpTooltip.examples.gallery')
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('helpTooltip.examples.collection')
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('helpTooltip.examples.group')
          })]
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('helpTooltip.moreHelp')
        }), /*#__PURE__*/_jsx(UILink, {
          className: "m-left-1",
          target: "_blank",
          href: "http://designers.hubspot.com/docs/modules/repeating-fields-and-loops",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('helpTooltip.learnMore')
          })
        })]
      })
    });

    return /*#__PURE__*/_jsx(UIHelpIcon, {
      title: title,
      color: BATTLESHIP,
      tooltipPlacement: "top left",
      tooltipProps: {
        headingText: text(i18nKey('helpTooltip.headingText')),
        placement: 'top left',
        className: 'field-edit-help-tooltip',
        use: 'longform'
      }
    });
  }

  renderOccurrenceTitle() {
    const occurrenceErrors = pick(this.props.fieldErrors, OCCURRENCE);
    const occurrenceWarnings = pick(this.props.fieldWarnings, OCCURRENCE);
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "end",
      children: [/*#__PURE__*/_jsx(ErrorIcon, {
        className: "m-right-2",
        fieldErrors: occurrenceErrors,
        i18nKey: sidebarI18nKey('contentOptions.hasFieldError')
      }), /*#__PURE__*/_jsx(WarningIcon, {
        className: "m-right-2",
        fieldWarnings: occurrenceWarnings,
        i18nKey: sidebarI18nKey('contentOptions.hasFieldWarning')
      }), /*#__PURE__*/_jsx("span", {
        className: "m-right-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('accordionLabel')
        })
      }), this.renderOccurrenceHelpTooltip()]
    });
  }

  renderSortingLabelFieldPreview(field) {
    if (!field) {
      return null;
    }

    const resources = pick(this.props, Object.values(RESOURCES_BY_TYPE || {}));

    const defaultValueLabel = /*#__PURE__*/_jsx(OccurrenceLabel, {
      defaultLabel: field.label,
      field: field,
      resources: resources
    });

    const preview = /*#__PURE__*/_jsx("div", {
      className: "cmv2-occurrence",
      children: /*#__PURE__*/_jsx(UITable, {
        className: "occurrences-table m-all-0",
        hover: false,
        children: /*#__PURE__*/_jsx("tbody", {
          children: /*#__PURE__*/_jsx(OccurrencesTableRow, {
            deletable: false,
            fieldId: field.id,
            index: 0,
            isPreview: true,
            value: defaultValueLabel
          })
        })
      })
    });

    return /*#__PURE__*/_jsx(UITooltip, {
      className: "repeater-options-tooltip",
      placement: "top left",
      title: preview,
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "link",
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('sortingLabel.previewLink')
          })
        })
      })
    });
  }

  renderSortingLabelFieldSelect() {
    const {
      field,
      readOnly
    } = this.props;
    const {
      children = []
    } = field;

    if (field.type !== GROUP || !children.length) {
      return null;
    }

    const options = children.map(_child => {
      return {
        text: _child.label,
        value: _child.id
      };
    });
    const {
      occurrence: {
        sorting_label_field: fieldId
      }
    } = this.state;
    const sortingLabelField = fieldId && children.find(_child => _child.id === fieldId);

    const label = /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx("span", {
        className: "m-right-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('sortingLabel.label')
        })
      }), /*#__PURE__*/_jsx(UIHelpIcon, {
        title: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('sortingLabel.tooltip')
          })
        }),
        color: BATTLESHIP,
        tooltipProps: {
          placement: 'top left'
        }
      })]
    });

    return /*#__PURE__*/_jsx(UIFormControl, {
      help: this.renderSortingLabelFieldPreview(sortingLabelField),
      label: label,
      children: /*#__PURE__*/_jsx(SidebarInput, {
        clearable: true,
        inputComponent: UISelect,
        onChange: this.onSortingLabelFieldChange,
        options: options,
        placeholder: text(i18nKey('sortingLabel.placeholder')),
        value: sortingLabelField ? fieldId : null,
        disabled: readOnly
      })
    });
  }

  render() {
    const {
      fieldErrors,
      fieldWarnings,
      field,
      onOpenChange,
      open,
      readOnly
    } = this.props;
    const {
      enabled,
      occurrence
    } = this.state;
    const {
      min,
      max,
      default: defaultOccurrence
    } = occurrence;
    const minErrors = [];
    const maxErrors = [];
    const otherErrors = [];

    if (fieldErrors[OCCURRENCE]) {
      fieldErrors[OCCURRENCE].forEach(error => {
        switch (error.type) {
          case MODULE_VALIDATION_OCCURRENCE_MIN_LESS_THAN_ZERO:
            {
              minErrors.push(error);
              break;
            }

          case MODULE_VALIDATION_OCCURRENCE_MAX_LESS_THAN_ONE:
            {
              maxErrors.push(error);
              break;
            }

          case MODULE_VALIDATION_OCCURRENCE_MAX_LESS_THAN_MIN:
            {
              maxErrors.push(error);
              break;
            }

          default:
            otherErrors.push(error);
        }
      });
    }

    const minWarnings = [];
    const maxWarnings = [];
    const otherWarnings = [];

    if (fieldWarnings[OCCURRENCE]) {
      fieldWarnings[OCCURRENCE].forEach(error => {
        switch (error.type) {
          case MODULE_VALIDATION_OCCURRENCE_MIN_LESS_THAN_ZERO:
            {
              minWarnings.push(error);
              break;
            }

          case MODULE_VALIDATION_OCCURRENCE_MAX_LESS_THAN_ONE:
            {
              maxWarnings.push(error);
              break;
            }

          case MODULE_VALIDATION_OCCURRENCE_MAX_LESS_THAN_MIN:
            {
              maxWarnings.push(error);
              break;
            }

          default:
            otherWarnings.push(error);
        }
      });
    }

    const toggle = /*#__PURE__*/_jsx(UIToggle, {
      checked: enabled,
      "data-test-id": "module-field-editor-repeater-toggle",
      onChange: this.onToggleClick,
      size: "extra-small",
      readOnly: readOnly
    });

    return /*#__PURE__*/_jsx(ToolExpandableSection, {
      actions: toggle,
      containerClassName: "field-edit-accordion-container",
      titleText: this.renderOccurrenceTitle(),
      onOpenChange: onOpenChange,
      open: open,
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsxs(ToolFieldset, {
          children: [/*#__PURE__*/_jsx(FormControlWithErrors, {
            className: "m-bottom-3",
            errors: minErrors.concat(maxErrors),
            warnings: minWarnings.concat(maxWarnings),
            label: text(i18nKey('countLimitsLabel')),
            children: /*#__PURE__*/_jsxs(UIFlex, {
              align: "end",
              children: [/*#__PURE__*/_jsx(Small, {
                use: "help",
                children: /*#__PURE__*/_jsx(FormControlWithErrors, {
                  error: !!minErrors.length,
                  label: text(i18nKey('minLabel')),
                  children: /*#__PURE__*/_jsx(SidebarInput, {
                    "data-test-id": "module-field-editor-repeater-min",
                    inputComponent: UINumberInput,
                    onChange: (...partialArgs) => this.onNumberValueChange('min', ...partialArgs),
                    placeholder: text(i18nKey('minPlaceholder')) // Only display min value when toggle is enabled
                    ,
                    value: min,
                    min: 0,
                    disabled: readOnly
                  })
                })
              }), /*#__PURE__*/_jsx("span", {
                className: "p-x-2 p-y-1",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: i18nKey('toLabel')
                })
              }), /*#__PURE__*/_jsx(Small, {
                use: "help",
                children: /*#__PURE__*/_jsx(FormControlWithErrors, {
                  error: !!maxErrors.length,
                  label: text(i18nKey('maxLabel')),
                  children: /*#__PURE__*/_jsx(SidebarInput, {
                    "data-test-id": "module-field-editor-repeater-max",
                    inputComponent: UINumberInput,
                    onChange: (...partialArgs) => this.onNumberValueChange('max', ...partialArgs),
                    placeholder: text(i18nKey('maxPlaceholder')),
                    value: max,
                    min: 1,
                    disabled: readOnly
                  })
                })
              })]
            })
          }), /*#__PURE__*/_jsx(FormControlWithErrors, {
            errors: otherErrors,
            warnings: otherWarnings,
            label: text(i18nKey('defaultCountLabel')),
            children: /*#__PURE__*/_jsx(SidebarInput, {
              "data-test-id": "module-field-editor-repeater-default",
              inputComponent: UINumberInput,
              onChange: (...partialArgs) => this.onNumberValueChange('default', ...partialArgs),
              placeholder: text(i18nKey('defaultPlaceholder')) // Only display default value when toggle enabled
              ,
              value: defaultOccurrence,
              min: 0,
              disabled: readOnly
            })
          }), this.renderSortingLabelFieldSelect()]
        })
      })
    }, `${field.id}-repeater-options`);
  }

}

RepeaterOptions.propTypes = {
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldErrors: PropTypes.object,
  fieldWarnings: PropTypes.object,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  readOnly: PropTypes.bool
};
RepeaterOptions.defaultProps = {
  fieldErrors: {},
  fieldWarnings: {}
};
export default RepeaterOptions;